/**
 * Scheme detector: `protocol:` per RFC 3986 — a letter followed by any
 * combination of letters, digits, `+`, `.`, or `-` then `:`.
 */
const SCHEME_PATTERN = /^([a-z][a-z0-9+.-]*):/i

/**
 * Allowlist of schemes that are safe to forward into `<a href>` for
 * external navigation. `javascript:` / `data:` / `vbscript:` etc. are
 * intentionally **not** on this list — link-attachment data is
 * effectively user-controlled, so passing them through would let a
 * recipient click execute attacker-supplied code or markup.
 */
const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'sms'])

/**
 * Normalize a user-supplied URL into something safe to assign to
 * `<a href>` for external navigation.
 *
 * Link attachments / link apps always point at external destinations
 * (Spotify, TikTok, FAQ links, bare-hostname Linktree URLs like
 * `tr.ee/briemix`, etc.). Without normalization, a bare hostname is
 * treated as a relative path by the browser and clicks navigate within
 * the host site (e.g. `https://linktr.ee/admin/tr.ee/briemix`) instead
 * of opening the intended destination.
 *
 * Rules:
 *   - Empty / whitespace-only → returns `undefined` (no href).
 *   - Explicit scheme in the safe allowlist (`http`, `https`, `mailto`,
 *     `tel`, `sms`) → returned trimmed.
 *   - Explicit scheme **not** on the allowlist (`javascript:`, `data:`,
 *     `vbscript:`, custom protocols, …) → returns `undefined` so the
 *     shell falls back to a non-navigational chrome instead of letting
 *     an attacker-controlled URL execute on click.
 *   - Protocol-relative (`//example.com/…`) → returned as-is; browsers
 *     resolve these against the current page's scheme.
 *   - Site-relative path (`/admin/…`) → returned as-is so consumers can
 *     still opt into in-app navigation if they really want to.
 *   - Bare hostname or anything else → `https://` is prepended so the
 *     browser treats it as an external URL.
 */
export function normalizeExternalHref(value?: string): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (trimmed === '') return undefined

  const schemeMatch = SCHEME_PATTERN.exec(trimmed)
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase()
    return SAFE_SCHEMES.has(scheme) ? trimmed : undefined
  }

  if (trimmed.startsWith('//')) return trimmed
  if (trimmed.startsWith('/')) return trimmed
  return `https://${trimmed}`
}
